import { useEffect, useState } from 'react'
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  type: ToastType
  message: string
  duration?: number
}

interface ToastProps {
  toast: Toast
  onClose: (id: string) => void
}

const icons = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
}

const colors = {
  success: 'bg-green-50 border-green-200 text-green-800',
  error: 'bg-red-50 border-red-200 text-red-800',
  info: 'bg-blue-50 border-blue-200 text-blue-800',
  warning: 'bg-orange-50 border-orange-200 text-orange-800',
}

export function ToastItem({ toast, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false)
      setTimeout(() => onClose(toast.id), 300)
    }, toast.duration || 3000)

    return () => clearTimeout(timer)
  }, [toast.id, toast.duration, onClose])

  const Icon = icons[toast.type]

  return (
    <div
      className={`fixed bottom-20 left-4 right-4 md:left-auto md:max-w-sm transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
      }`}
    >
      <div className={`card border-2 ${colors[toast.type]} flex items-center`}>
        <Icon className="mr-3 flex-shrink-0" size={20} />
        <div className="flex-1 text-sm">{toast.message}</div>
        <button
          onClick={() => {
            setIsVisible(false)
            setTimeout(() => onClose(toast.id), 300)
          }}
          className="ml-2 flex-shrink-0"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  )
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([])

  useEffect(() => {
    const handleToast = (event: CustomEvent<Toast>) => {
      const toast = {
        ...event.detail,
        id: event.detail.id || Date.now().toString(),
      }
      setToasts((prev) => [...prev, toast])
    }

    window.addEventListener('toast' as any, handleToast as EventListener)

    return () => {
      window.removeEventListener('toast' as any, handleToast as EventListener)
    }
  }, [])

  const handleClose = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }

  return (
    <div className="fixed bottom-20 left-0 right-0 z-50 pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onClose={handleClose} />
        </div>
      ))}
    </div>
  )
}

export function showToast(type: ToastType, message: string, duration?: number) {
  const customEvent = new CustomEvent('toast', {
    detail: {
      type,
      message,
      duration,
      id: Date.now().toString(),
    },
  })
  window.dispatchEvent(customEvent)
}

